import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export interface TaskDependency {
  id: string;
  task_id: string;
  depends_on_task_id: string;
  dependency_type: 'blocks' | 'blocked-by' | 'relates-to';
  created_at: string;
}

export interface ListDependenciesParams {
  organization_id: string;
  project_id?: string;
  task_id?: string;
}

export interface CreateDependencyInput {
  organization_id: string;
  task_id: string;
  depends_on_task_id: string;
  dependency_type?: 'blocks' | 'blocked-by' | 'relates-to';
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/dependencies`;

export const dependenciesService = {
  async list(params: ListDependenciesParams): Promise<{ data: TaskDependency[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: [], error: result?.error || 'Failed to fetch dependencies' };
      }

      return { data: (result.data || []) as TaskDependency[], error: null };
    } catch (err: any) {
      return { data: [], error: err.message || 'Network error' };
    }
  },

  async create(input: CreateDependencyInput): Promise<{ data: TaskDependency | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to create dependency' };
      }

      return { data: (result.data || null) as TaskDependency | null, error: null };
    } catch (err: any) {
      return { data: null, error: err.message || 'Network error' };
    }
  },

  async delete(dependencyId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(`${API_BASE_URL}/${dependencyId}?${queryParams.toString()}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to delete dependency' };
      }

      return { error: null };
    } catch (err: any) {
      return { error: err.message || 'Network error' };
    }
  },
};

export default dependenciesService;
